/* Try drawer — workflows are pure request→response, so: paste a sample
 * input, see { output, outcome, steps } instantly, iterate. */
import React, {
    useMemo,
    useState
} from 'react';
import {
    Loader2,
    Play,
    X
} from 'lucide-react';
import {
    appEngineService,
    describeError,
    extractValidatorErrors
} from '../../api/appEngine.service';
import {
    useAppStudio
} from '../../AppStudioContext';
import CodeMirrorJson from '../../shared/CodeMirrorJson';
import JsonTree from '../../shared/JsonTree';
import {
    OutcomeBadge
} from '../../shared/badges';
import type {
    TryWorkflowResult
} from '../../api/types';

/** Skeleton input from the workflow's declared input contract. */
function skeletonInput(workflowText: string): string {
    try {
        const doc = JSON.parse(workflowText);
        const fields = doc?.input?.fields;
        if (Array.isArray(fields) && fields.length) {
            const obj: Record<string, any> = {};
            for (const f of fields) {
                if (f?.name) obj[f.name] = null;
            }
            return JSON.stringify(obj, null, 2);
        }
    } catch { /* fall through */ }
    return '{\n}';
}

function StepsList({
    steps
}: {
    steps: TryWorkflowResult['steps'];
}) {
    const entries: Array<[string, any]> = useMemo(() => {
        if (!steps) return [];
        if (Array.isArray(steps)) {
            return steps.map((s: any, i: number) => [s?.id || `step ${i + 1}`, s]);
        }
        return Object.entries(steps);
    }, [steps]);
    if (entries.length === 0) return null;
    return (
        <div>
            <p className="aps-meta" style={{
                margin: '0 0 8px'
            }}>
                Steps
            </p>
            <div style={{
                display: 'flex',
                flexDirection: 'column',
                gap: 8
            }}>
                {entries.map(([id, s]) => (
                    <div key={id} className="aps-card" style={{
                        padding: '10px 14px'
                    }}>
                        <div style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 8,
                            marginBottom: 6
                        }}>
                            <span className="aps-mono" style={{
                                fontWeight: 600
                            }}>
                                {id}
                            </span>
                            <OutcomeBadge outcome={s?.outcome} />
                        </div>
                        <JsonTree value={s?.output ?? null} />
                    </div>
                ))}
            </div>
        </div>
    );
}

export default function TryDrawer({
    workflowId,
    workflowText,
    onClose
}: {
    workflowId: string;
    workflowText: string;
    onClose: () => void;
}) {
    const {
        tenancy
    } = useAppStudio();
    const [input, setInput] = useState(() => skeletonInput(workflowText));
    const [busy, setBusy] = useState(false);
    const [result, setResult] = useState<TryWorkflowResult | null>(null);
    const [error, setError] = useState<string | null>(null);

    const run = async () => {
        let parsed: any;
        try {
            parsed = JSON.parse(input);
        } catch (e: any) {
            setError(`Input is not valid JSON: ${e?.message || ''}`);
            return;
        }
        setBusy(true);
        setError(null);
        try {
            const res = await appEngineService.executeWorkflow(tenancy, workflowId, parsed);
            setResult(res);
        } catch (err: any) {
            const errs = extractValidatorErrors(err);
            setError(errs ? errs.join('\n') : describeError(err));
            setResult(null);
        } finally {
            setBusy(false);
        }
    };

    return (
        <>
            <div className="aps-drawer-overlay" onClick={onClose} />
            <aside className="aps-drawer" role="dialog" aria-label={`Try ${workflowId}`}>
                <div className="aps-drawer-head">
                    <Play size={16} color="var(--aps-primary)" />
                    <span style={{
                        fontWeight: 700
                    }}>
                        Try
                    </span>
                    <span className="aps-mono" style={{
                        color: 'var(--aps-text-muted)'
                    }}>
                        {workflowId}
                    </span>
                    <span style={{
                        marginLeft: 'auto'
                    }} />
                    <button className="aps-icon-btn" onClick={onClose} title="Close">
                        <X size={16} />
                    </button>
                </div>
                <div className="aps-drawer-body">
                    <div>
                        <p className="aps-meta" style={{
                            margin: '0 0 8px'
                        }}>
                            Input
                        </p>
                        <div className="aps-editor-frame" style={{
                            height: 180,
                            flex: 'none'
                        }}>
                            <CodeMirrorJson value={input} onChange={setInput} />
                        </div>
                    </div>
                    <button className="aps-btn aps-btn-primary" disabled={busy} onClick={run}>
                        {busy ? <Loader2 size={15} className="aps-spin" /> : <Play size={15} />}
                        {busy ? 'Running…' : 'Run workflow'}
                    </button>
                    {error && (
                        <div className="aps-errors">
                            <p className="aps-errors-title">Try failed</p>
                            <ul>
                                {error.split('\n').map((l, i) => <li key={i}>{l}</li>)}
                            </ul>
                        </div>
                    )}
                    {result && (
                        <>
                            <div style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 10
                            }}>
                                <p className="aps-meta" style={{
                                    margin: 0
                                }}>
                                    Outcome
                                </p>
                                <OutcomeBadge outcome={result.outcome} />
                            </div>
                            <div>
                                <p className="aps-meta" style={{
                                    margin: '0 0 8px'
                                }}>
                                    Output
                                </p>
                                <div className="aps-card" style={{
                                    padding: '12px 14px'
                                }}>
                                    <JsonTree value={result.output ?? null} />
                                </div>
                            </div>
                            <StepsList steps={result.steps} />
                        </>
                    )}
                </div>
            </aside>
        </>
    );
}
